import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../lib/apiClient';
import queryClient from '../../../lib/queryClient';
import type { User } from '../types';

interface UpdateUserRequest {
  userAuthUuid: string;
  userData: Partial<User>;
  currentUser: User;
}

interface GraphQLResponse {
  data?: {
    users?: {
      code: string;
      success: boolean;
      message: string;
      nodes: Array<{
        id: string;
        authServiceUuid: string;
        emailAddress: string;
        supplierReadOnly: boolean;
        personalInfo: {
          firstName: string;
          lastName: string;
          primaryPhoneNumber: string | null;
          secondaryPhoneNumber: string | null;
          fiscalYearEnd: string | null;
          arDepartmentEmailAddress: string | null;
          address: {
            country: string | null;
            postalCode: string | null;
          };
          industry: string | null;
          locale: string | null;
          title: string | null;
          role: string | null;
        };
      }>;
      errors?: Array<{
        code: string;
        msg: string;
      }>;
    };
  };
}

const updateUser = async ({ userAuthUuid, userData, currentUser }: UpdateUserRequest) => {
  const graphqlMutation = {
    query: `mutation updateUsers($users: [UserInput!]) {
  users(users: $users) {
    code
    success
    message
    nodes {
      id
      authServiceUuid: id
      emailAddress
      supplierReadOnly
      personalInfo {
        firstName
        lastName
        primaryPhoneNumber
        secondaryPhoneNumber
        fiscalYearEnd
        arDepartmentEmailAddress
        address {
          country
          postalCode
        }
        industry
        locale
        title
        role
      }
    }
    errors {
      code
      msg
    }
  }
}`,
    variables: {
      users: [
        {
          id: userAuthUuid,
          supplierReadOnly: userData.supplierReadOnly ?? currentUser.supplierReadOnly,
          personalInfo: {
            firstName: userData.firstName ?? currentUser.firstName,
            lastName: userData.lastName ?? currentUser.lastName,
            primaryPhoneNumber: userData.primaryPhoneNumber !== undefined ? userData.primaryPhoneNumber : null,
            secondaryPhoneNumber: userData.secondaryPhoneNumber !== undefined ? userData.secondaryPhoneNumber : null,
            fiscalYearEnd: userData.fiscalYearEnd !== undefined ? userData.fiscalYearEnd : null,
            arDepartmentEmailAddress:
              userData.arDepartmentEmailAddress !== undefined ? userData.arDepartmentEmailAddress : null,
            address: {
              country: userData.country !== undefined ? userData.country : null,
              postalCode: userData.postalCode !== undefined ? userData.postalCode : null,
            },
            industry: userData.industry !== undefined ? userData.industry : null,
            locale: userData.language !== undefined ? userData.language : null,
            title: userData.title !== undefined ? userData.title : null,
            role: userData.role !== undefined ? userData.role : null,
          },
        },
      ],
    },
  };

  const result = await apiClient()
    .post('api/user-details/graphql', {
      json: graphqlMutation,
    })
    .json<GraphQLResponse>();

  if (!result.data?.users?.success) {
    const errorMsg = result.data?.users?.errors?.[0]?.msg || 'Failed to update user';
    throw new Error(errorMsg);
  }

  return result.data.users;
};

export const useUpdateUserMutation = () => {
  return useMutation({
    mutationFn: updateUser,
    onSuccess: (_data, variables) => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: ['userDetails', variables.userAuthUuid] });

      // Also invalidate divisions query if it exists
      queryClient.invalidateQueries({ queryKey: ['userDivisions', variables.userAuthUuid] });
    },
  });
};
